import React from "react";
import {
    Box,
    IconButton,
} from "@mui/material";
import { Link, useSearchParams } from "react-router-dom";

import BreadcrumbLayout from "layouts/BreadcrumbLayout";
import AppTable from "components/Shared/AppTable";


import { useTablePagination } from "hooks";
import { useLazyListShipmentForReturnedQuery } from "store/services/reportApi";
import AppTableDateTime from "components/Shared/AppTableDateTime";
import HighlightedText from "components/Shared/HighlightedText";
import { InfoOutlined } from "@mui/icons-material";
import ExportShipmentReturnExcel from "./PaymentReports/VendorReports/ExportShipmentReturnExcel";

const ReturnedShipmentReportListScreen = () => {

    const { currentPage, perPage, handlePageChange, handlePerRowsChange } =
        useTablePagination();

    const [searchParams] = useSearchParams();
    const keyword = searchParams.get("search") || "";

    const breadcrumbs = [
        {
            title: "Reports",
            path: "/app/crm/reports/list",
        },
        {
            title: "Returned Shipment Report",
        },
    ];

    const columns = [
        {
            name: "Shipment ID",
            selector: (row) => row.shipmentId,
            width: "220px",
            cell: (row) => {
                const shiId = row?.shipmentId?.replace(/(.{4})/g, "$1-").slice(0, -1);
                const displayId = `${shiId}${row.IsReturning ? " (R)" : ""}`;
                const path = `/app/crm/shipment/${row?._id}/detail`;

                return (
                    <Link
                        title={displayId}
                        to={path}
                        className="line-clamp-2 hover:text-blue-600 hover:underline dark:hover:text-blue-300"
                    >
                        {displayId ? (
                            <HighlightedText text={displayId} highlight={keyword} />
                        ) : (
                            "N/A"
                        )}
                    </Link>
                );
            },
        },
        {
            name: "Sender",
            width: "220px",
            selector: (row) =>
                row?.customerId?.customerUser?.name || row?.vendor?.shopName || "N/A",
            cell: (row) => (
                <Box
                    sx={{
                        maxWidth: "600px",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                    }}
                >
                    {row?.customerId?.customerUser?.name
                        ? `CUS: ${row?.customerId?.customerUser?.name}`
                        : row?.vendor?.shopName
                            ? `VEN: ${row.vendor.shopName}`
                            : "N/A"}
                </Box>
            ),
        },
        {
            name: "Receiver",
            width: "200px",
            selector: (row) => row?.receiver?.name,
            cell: (row) => (
                <div>
                    <span className="text-sm">{row?.receiver?.name}</span>
                    <br />
                    <span className="text-sm text-gray-500 dark:text-gray-300">
                        {row?.receiver?.phone}
                    </span>
                </div>
            ),
        },
        {
            name: "From",
            selector: (row) => row?.sourceBranch?.name,
            cell: (row) => row?.sourceBranch?.name || "N/A",
        },
        {
            name: "To",
            selector: (row) => row?.destinationBranch?.name,
            cell: (row) => row?.destinationBranch?.name || "N/A",
        },
        {
            name: "Created At",
            selector: (row) => <AppTableDateTime date={row?.createdAt} />,
        },
        {
            name: "Actions",
            cell: (row) => (
                <Box display="flex" justifyContent="center" alignItems="center" gap={1}>
                    <Link to={`/app/crm/shipment/${row?._id}/detail`}>
                        <IconButton size="small" aria-label="View Shipment" color="primary">
                            <InfoOutlined />
                        </IconButton>
                    </Link>
                </Box>
            ),
            ignoreRowClick: true,
            right: true,
        },
    ];

    const [fetchShipment, { data: shipmentRes, isLoading, isFetching, error }] =
        useLazyListShipmentForReturnedQuery();

    // Handle numeric-keyed response by converting to array
    const shipmentData = React.useMemo(() => {
        if (!shipmentRes?.data) return [];
        return Object.values(shipmentRes.data);
    }, [shipmentRes?.data]);

    React.useEffect(() => {
        fetchShipment({
            page: currentPage,
            limit: perPage,
            ...(keyword && { keyword }),
        });
    }, [currentPage, fetchShipment, keyword, perPage]);

    return (
        <BreadcrumbLayout breadcrumbs={breadcrumbs}>
            <BreadcrumbLayout.Error error={error} />
            <BreadcrumbLayout.Paper>
                <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2, p: 1 }}>
                    <ExportShipmentReturnExcel />
                </Box>
                <AppTable
                    columns={columns}
                    data={shipmentData}
                    progressPending={isLoading || isFetching}
                    paginationServer
                    paginationPerPage={perPage}
                    paginationTotalRows={shipmentRes?.totalItems ?? 0}
                    paginationDefaultPage={currentPage}
                    onChangePage={handlePageChange}
                    onChangeRowsPerPage={handlePerRowsChange}
                />
            </BreadcrumbLayout.Paper>
        </BreadcrumbLayout>
    );
};

export default ReturnedShipmentReportListScreen;